// Customer workspace sidebar, top to bottom:
//
//   Corvus Research lockup  the product mark (kept prominent at the
//                           owner's request; smaller on short screens) in
//                           a band the top bar's height, so their hairlines
//                           meet; at its right end the button that folds
//                           the sidebar to its icon rail
//   Workspace               one quiet line: its name (the id, as words
//                           when it spells them) · how many agents it runs
//                           (remembered from the last visit until known)
//   Record a meeting        an ACTION (R, named in its tooltip), not a
//                           destination
//   Nav                     Home / Work / Team / Workspace (SidebarNav),
//                           with counts where something waits
//   You                     the signed-in operator and Sign out
//
// The nav rows span the sidebar edge to edge (SidebarNav), so they have no
// corners to round; the Record button, counts and the fold button keep the
// app's own control shapes, like the search field and page buttons beside
// them.
//
// Material: the theme's sidebar glass (blur + bevel) in Glass; Earth and
// Midnight turn blur off globally (html[data-flat]) and read as a flat
// panel. Geometry is the same in every theme.
//
// Fitting real windows (a 1366x768 laptop leaves ~650px of viewport):
// three vertical steps, never overlapping — tall (> 750px: the 11 rows,
// the workspace line, Record and You need ~740px), short (701-750px) and
// shortest (<= 700px), each tighter than the last, so the whole nav fits
// down to ~620px; below that the nav scrolls on its own
// and keeps the current row in view. Spacing is in whole pixels (the root
// size is 15px, so rem steps would land borders on half pixels).
//
// Below 1024px wide (a tablet in portrait, a narrow window) the sidebar
// is a 64px rail: the logo's mark, an icon-only Record button, icon rows
// with tooltips and the operator's avatar. Wider than that the operator
// can fold it to the same rail and back: one visible button (at the logo
// band's right end; on the rail, under the mark) or "[". The choice is kept
// per browser (stores/ui). Both come from Tailwind's `rail:` variant, so
// the rail has one set of classes.
//
// In the Mac app (native/desktop.ts) the logo band gives way to the
// window's top row (MacWindowControls: room for the window buttons, then
// the fold button, Back and Forward) and the rail is 80px wide.

import { useRef } from "react";
import { LogOut, Mic } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";

import { useOperatorName } from "@/api/passwords";
import { AgentAvatar } from "@/components/ui/AgentAvatar";
import { Button } from "@/components/ui/Button";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { humanizeSlug, pluralize } from "@/lib/format";
import { cn } from "@/lib/utils";
import { isMacShell } from "@/native/desktop";
import { useAuth } from "@/stores/auth";
import { useUi } from "@/stores/ui";
import { colors } from "@/theme";

import { MacSidebarTopRow } from "./MacWindowControls";
import { RAIL_QUERY, SidebarNav } from "./SidebarNav";
import { SidebarToggle } from "./SidebarToggle";
import { useShortcut } from "./shortcuts";
import { useWorkspaceAgentCount } from "./useWorkspaceAgentCount";
import { useSignOut } from "@/features/meetings/recorder/SignOutGuard";

const SIDEBAR_STYLE: React.CSSProperties = {
  background: colors.sidebarBg,
  backdropFilter: "blur(40px) saturate(140%)",
  WebkitBackdropFilter: "blur(40px) saturate(140%)",
  boxShadow: [
    `inset 0 1px 0 ${colors.bevelTop}`,
    `inset -1px 0 0 ${colors.sidebarEdge}`,
    "var(--shadow-sidebar-cast)",
  ].join(", "),
};

interface CustomerSidebarProps {
  customerId: string;
}

export function CustomerSidebar({ customerId }: CustomerSidebarProps) {
  const collapsed = useUi((s) => s.sidebarCollapsed);
  const toggle = useUi((s) => s.toggleSidebar);
  const narrow = useMediaQuery(RAIL_QUERY);
  // "[" anywhere outside a text field, remote desktop, menu or dialog.
  // Below 1024px the rail is forced: nothing to fold, so no key and no button.
  useShortcut("[", toggle, !narrow);
  // A click on the button moves it (band ⇄ rail): focus follows it.
  const refocus = useRef(false);
  const fold = (
    <SidebarToggle
      collapsed={collapsed}
      refocus={refocus}
      onToggle={() => {
        refocus.current = true;
        toggle();
      }}
    />
  );
  // In the Mac app the window's top row replaces the logo band, and the
  // rail is wider (92px) for the window buttons (MacWindowControls).
  if (isMacShell()) {
    return (
      <aside
        aria-label="Workspace navigation"
        data-rail={collapsed || undefined}
        className="relative flex h-full w-sidebar shrink-0 flex-col rail:w-[92px]"
        style={SIDEBAR_STYLE}
      >
        <MacSidebarTopRow />
        <WorkspaceIdentity customerId={customerId} />
        <RecordAction />
        <SidebarNav customerId={customerId} />
        <UserIdentity customerId={customerId} />
      </aside>
    );
  }
  return (
    <aside
      aria-label="Workspace navigation"
      data-rail={collapsed || undefined}
      className="relative flex h-full w-sidebar shrink-0 flex-col rail:w-[64px]"
      style={SIDEBAR_STYLE}
    >
      <BrandLockup fold={narrow || collapsed ? null : fold} />
      {/* On the rail the expand button sits under the mark, with a hairline
          under it so it does not read as the first nav item. */}
      {!narrow && collapsed && <div className="mx-[16px] flex shrink-0 justify-center border-b border-line-subtle py-[10px]">{fold}</div>}
      <WorkspaceIdentity customerId={customerId} />
      <RecordAction />
      <SidebarNav customerId={customerId} />
      <UserIdentity customerId={customerId} />
    </aside>
  );
}

function BrandLockup({ fold }: { fold: React.ReactNode }) {
  return (
    <div
      className={cn(
        // The top bar's height (h-header) and hairline at every window
        // height, so the sidebar and the bar share one horizon. The fold
        // button's edge lines up with the counts' below it (24px in).
        "flex h-header shrink-0 items-center justify-between gap-3 border-b border-line-subtle px-[24px] rail:justify-center rail:px-0",
      )}
    >
      {/* On the rail only the square mark shows: the wordmark is cropped
          off (the mark is the first 276 of the logo's 939 units). */}
      <img
        src="/corvus-logo.svg"
        alt="Corvus Research"
        // The height step applies to the full sidebar only (lg:); on a rail
        // the operator chose, the rail's fixed 32px mark outranks it
        // (data-rail adds specificity).
        className={cn(
          "w-auto opacity-95 lg:h-[32px] lg:[@media(max-height:700px)]:h-[28px]",
          "rail:h-[32px] rail:w-[32px] rail:object-cover rail:object-left",
        )}
        draggable={false}
      />
      {fold}
    </div>
  );
}

// The workspace this session belongs to. The API has no display name for
// a workspace, so the id is the name: as words when it spells them
// ("sentient-capital" → "Sentient Capital"), otherwise exactly as typed
// ("corvusresearch" — its casing is not guessed at), then the agent count,
// quieter, on the same line. No liveness claim: there is no per-agent
// presence.
function WorkspaceIdentity({ customerId }: { customerId: string }) {
  // Remembered from the last visit until the agent lists answer.
  const agents = useWorkspaceAgentCount(customerId);
  const id = customerId.trim();
  const name = /[-_.\s]/.test(id) ? humanizeSlug(id, null, { titleCase: true }) : id;
  // While nothing is known, the name stands alone.
  const count = agents === null ? null : agents === 0 ? "No agents yet" : pluralize(agents, "agent");
  if (!name) return null;
  return (
    <div
      className={cn(
        "shrink-0 truncate px-[24px] pb-[12px] pt-[12px] text-sm rail:hidden",
        "[@media(min-height:701px)_and_(max-height:750px)]:pb-[8px]",
        // The shortest step (<= 700px) gives its room to the nav: the
        // operator block's tooltip still names the workspace.
        "[@media(max-height:700px)]:hidden",
      )}
      title={`Workspace: ${customerId}`}
    >
      <span className="font-medium text-fg">{name}</span>
      {count && <span className="text-fg-subtle"> · {count}</span>}
    </div>
  );
}

function RecordAction() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const here = pathname === "/record";
  return (
    <div
      className={cn(
        "shrink-0 px-[12px] pb-[12px]",
        "[@media(min-height:701px)_and_(max-height:750px)]:pb-[8px] [@media(max-height:700px)]:pb-[6px]",
        // Where the workspace line is hidden (the rail, the shortest step),
        // the button keeps its distance from the logo band's hairline.
        "rail:pt-[12px] [@media(max-height:700px)]:pt-[8px]",
      )}
    >
      <Button
        variant="secondary"
        size="md"
        // On /record it reads as where you are (the accent "on" surface),
        // announced as the current page rather than as a toggle.
        pressed={here}
        aria-pressed={undefined}
        aria-current={here ? "page" : undefined}
        className="w-full justify-start rail:justify-center rail:gap-0 rail:px-0"
        onClick={() => navigate("/record")}
        title="Record a meeting"
      >
        {/* Accent, like every other Record entry point: red is kept for
            danger (and a recording in progress, which only exists on
            /record, where this button reads as the current page). */}
        <Mic aria-hidden className={cn("h-4 w-4 shrink-0", !here && "text-accent-fg")} strokeWidth={1.75} />
        <span className="rail:sr-only">Record a meeting</span>
      </Button>
    </div>
  );
}

function UserIdentity({ customerId }: { customerId: string }) {
  // Asks first while a meeting is being recorded (features/meetings/recorder/SignOutGuard.tsx).
  const { signOut, dialog } = useSignOut();
  const user = useAuth((s) => s.user);
  // A Google login's name is its email: show the person's name (Settings → General) instead.
  const byEmail = !!user && user.includes("@");
  const operator = useOperatorName(byEmail ? customerId : null);
  const name = (byEmail && operator.data?.name?.trim()) || user || "Operator";
  return (
    <div
      className={cn(
        "flex shrink-0 items-center gap-3 border-t border-line-subtle px-[20px] py-[16px]",
        "[@media(min-height:701px)_and_(max-height:750px)]:py-[12px] [@media(max-height:700px)]:py-[8px]",
        "rail:flex-col rail:gap-[8px] rail:px-0",
      )}
    >
      <span className="flex shrink-0" title={`${name} · Operator · ${customerId}`}>
        <AgentAvatar name={name} size={32} />
      </span>
      <div className="min-w-0 flex-1 rail:hidden">
        <div className="truncate text-sm font-medium text-fg" title={byEmail && user ? user : name}>
          {name}
        </div>
        {/* The role, unless the sign-in name already says it ("operator"). */}
        {name.toLowerCase() !== "operator" && <div className="truncate text-xs text-fg-subtle">Operator</div>}
      </div>
      <Button variant="ghost" size="icon" onClick={signOut} title="Sign out" aria-label="Sign out">
        <LogOut aria-hidden className="h-4 w-4" strokeWidth={1.5} />
      </Button>
      {dialog}
    </div>
  );
}
